import React from "react";
import { Link } from "react-router-dom";
import {
  FaFacebookF,
  FaInstagram,
  FaXTwitter,
  FaLinkedinIn,
  FaYoutube,
} from "react-icons/fa6";
import { useLanguage } from "../../../contexts/LanguageContext";

const logoUrl = process.env.PUBLIC_URL
  ? `${process.env.PUBLIC_URL}/logojes.png`
  : "/logojes.png";

const SocialIcon = ({ href, label, children, className = "" }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`w-10 h-10 rounded-full border border-ink-900/10 flex items-center justify-center text-ink-500 hover:border-ember-600 hover:text-ember-600 transition-colors duration-150 ${className}`}
    aria-label={label}
  >
    {children}
  </a>
);

/**
 * Footer visiteur modernise : structure claire, visuel epure.
 */
const FooterSection = ({ onViewRecipes }) => {
  const { t } = useLanguage();

  const currentYear = new Date().getFullYear();

  return (
    <footer className="mt-4 sm:mt-6 w-full">
        <div className="rounded-2xl border border-ink-900/10 bg-white p-6 sm:p-8 lg:p-10 text-ink-900">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 lg:gap-10">
            <div className="space-y-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2.5 text-ink-900 hover:opacity-90 transition-opacity"
              >
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-ember-500 to-ember-600 p-0.5">
                  <div className="h-full w-full rounded-[10px] bg-ink-900/85 flex items-center justify-center">
                    <img src={logoUrl} alt="" className="h-5 w-auto object-contain" />
                  </div>
                </div>
                <span className="font-display font-semibold text-lg">JesCook</span>
              </Link>
              <p className="text-ink-500 text-sm leading-relaxed max-w-xs">
                JesCook rassemble les cuisines du monde pour découvrir, partager et
                savourer.
              </p>
              <div>
                <h3 className="font-semibold text-xs uppercase tracking-[0.12em] text-ink-500 mb-3">
                  {t("stayConnected")}
                </h3>
                <div className="flex gap-2 flex-wrap">
                  <SocialIcon href="https://facebook.com" label="Facebook">
                    <FaFacebookF className="w-4 h-4" />
                  </SocialIcon>
                  <SocialIcon href="https://instagram.com" label="Instagram">
                    <FaInstagram className="w-4 h-4" />
                  </SocialIcon>
                  <SocialIcon href="https://twitter.com" label="Twitter">
                    <FaXTwitter className="w-4 h-4" />
                  </SocialIcon>
                  <SocialIcon href="https://linkedin.com" label="LinkedIn">
                    <FaLinkedinIn className="w-4 h-4" />
                  </SocialIcon>
                  <SocialIcon href="https://youtube.com" label="YouTube">
                    <FaYoutube className="w-4 h-4" />
                  </SocialIcon>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-xs uppercase tracking-[0.12em] text-ink-500 mb-4">
                Navigation
              </h3>
              <ul className="space-y-2.5 text-sm text-ink-700">
                <li>
                  <button
                    type="button"
                    onClick={onViewRecipes}
                    className="hover:text-ember-700 transition-colors text-left"
                  >
                    {t("recipes")}
                  </button>
                </li>
                <li>
                  <Link to="/auth" className="hover:text-ember-700 transition-colors">
                    {t("login")}
                  </Link>
                </li>
                <li>
                  <Link to="/favorites" className="hover:text-ember-700 transition-colors">
                    {t("favorites")}
                  </Link>
                </li>
                <li>
                  <Link to="/recipes" className="hover:text-ember-700 transition-colors">
                    {t("worldMenus")}
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-xs uppercase tracking-[0.12em] text-ink-500 mb-4">
                Informations
              </h3>
              <ul className="space-y-2.5 text-sm text-ink-700">
                <li>
                  <a href="#mentions" className="hover:text-ember-700 transition-colors">
                    {t("legal")}
                  </a>
                </li>
                <li>
                  <a
                    href="#confidentialite"
                    className="hover:text-ember-700 transition-colors"
                  >
                    {t("privacy")}
                  </a>
                </li>
                <li>
                  <a href="#cgu" className="hover:text-ember-700 transition-colors">
                    {t("terms")}
                  </a>
                </li>
                <li>
                  <a href="#cookies" className="hover:text-ember-700 transition-colors">
                    {t("cookies")}
                  </a>
                </li>
                <li>
                  <a href="#carrieres" className="hover:text-ember-700 transition-colors">
                    {t("careers")}
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-xs uppercase tracking-[0.12em] text-ink-500 mb-4">
                {t("contact")}
              </h3>
              <ul className="space-y-3 text-sm text-ink-700">
                <li className="space-y-1">
                  <p className="text-[11px] uppercase tracking-[0.1em] text-ink-400">
                    {t("address")}
                  </p>
                  <p>{t("addressValue")}</p>
                </li>
                <li className="space-y-1">
                  <p className="text-[11px] uppercase tracking-[0.1em] text-ink-400">
                    {t("email")}
                  </p>
                  <a
                    href={`mailto:${t("emailValue")}`}
                    className="hover:text-ember-700 transition-colors break-all"
                  >
                    {t("emailValue")}
                  </a>
                </li>
                <li className="space-y-1">
                  <p className="text-[11px] uppercase tracking-[0.1em] text-ink-400">
                    {t("phone")}
                  </p>
                  <a
                    href={`tel:${t("phoneValue").replace(/\s/g, "")}`}
                    className="hover:text-ember-700 transition-colors"
                  >
                    {t("phoneValue")}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 sm:mt-10 pt-5 border-t border-ink-900/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-ink-500 text-sm">
            <p>
              © {currentYear} JesCook. {t("allRightsReserved")}.
            </p>
            <p className="text-xs">
              {t("madeWith")} ❤️ {t("byPassionate")}
            </p>
          </div>
        </div>
    </footer>
  );
};

export default FooterSection;
